/**
 * PODZ-1757 / CUT-06 — Legacy Vue 2 → Vue 3 route redirect map.
 *
 * Single source of truth for every renamed/restructured legacy path. The app
 * router (`apps/customer-portal/src/router/index.ts`) spreads
 * `legacyRedirects.map(toRedirectRoute)` into its `routes` array (before the
 * catch-all); the Playwright walk spec
 * (`apps/customer-portal-e2e/src/legacy-redirects.spec.ts`) imports the SAME
 * `legacyRedirects` array via `@sera/core` and asserts each entry's landing
 * view. Because both consumers read the same array, the spec can never drift
 * from the registered routes.
 *
 * Load-bearing detail (RESEARCH §1): a string or `{ name, params }` redirect
 * DROPS the original query string. Only a FUNCTION redirect that explicitly
 * returns `query: to.query` preserves it. CONTEXT.md requires query
 * preservation (e.g. `/invoice/9?x=2` → `/invoices/9?x=2`), so every entry uses
 * a function redirect. The ONE exception is `/new_password`, which carries an
 * inert legacy `?token` the new app cannot use (the old reset link is dead); it
 * sets `dropQuery: true` so the user lands on a CLEAN `/password-reset` and
 * restarts the reset flow.
 */
import type { RouteRecordRaw } from 'vue-router';

export interface LegacyRedirect {
  /** Legacy Vue 2 path, with the same param tokens the legacy route used. */
  legacyPath: string;
  /** Target Vue 3 named route. */
  name: string;
  /** Param names to forward from the matched legacy path (e.g. ['id']). */
  forwardParams?: readonly string[];
  /**
   * Drop the incoming query instead of forwarding it. Set ONLY for
   * `/new_password`, whose legacy `?token` is inert in the Vue 3 app — the user
   * must restart the reset flow on a clean `/password-reset`.
   */
  dropQuery?: boolean;
}

/**
 * The 8 renamed/restructured legacy paths (CONTEXT.md D-coverage). Identical
 * paths that already resolve (`/`, `/login`, `/invoices`, `/quotes`,
 * `/profile`, `/schedule`, `/schedule-appointment`, `/appointment/:id`,
 * `/appointment-details/:token`) get NO entry; truly unknown paths fall through
 * to the router's existing `/:pathMatch(.*)*` → `/` catch-all.
 */
export const legacyRedirects: readonly LegacyRedirect[] = [
  { legacyPath: '/password_reset', name: 'password-reset' },
  // Token intentionally dropped — legacy reset links are dead post-cutover.
  { legacyPath: '/new_password', name: 'password-reset', dropQuery: true },
  { legacyPath: '/create_account', name: 'create-account' },
  { legacyPath: '/create_account_success', name: 'create-account-success' },
  { legacyPath: '/invoice/:id', name: 'invoice-detail', forwardParams: ['id'] },
  {
    legacyPath: '/quote_preview/:id',
    name: 'quote-detail',
    forwardParams: ['id'],
  },
  { legacyPath: '/service_history', name: 'appointments' },
  // Distinct from the pre-existing `/appointment-summary/:id` (HYPHEN) redirect
  // already in the router; this is the legacy UNDERSCORE path.
  {
    legacyPath: '/appointment_summary/:id',
    name: 'appointment-detail',
    forwardParams: ['id'],
  },
];

/**
 * Build a vue-router route record whose FUNCTION redirect forwards the named
 * params and (unless `dropQuery`) the original query string to the target
 * named route. The `query: to.query` branch is what preserves `?foo=1`; the
 * `query: {}` branch strips the inert `/new_password` token.
 */
export function toRedirectRoute(entry: LegacyRedirect): RouteRecordRaw {
  return {
    path: entry.legacyPath,
    redirect: (to) => ({
      name: entry.name,
      params: entry.forwardParams
        ? Object.fromEntries(entry.forwardParams.map((p) => [p, to.params[p]]))
        : {},
      query: entry.dropQuery ? {} : to.query,
    }),
  };
}
